import { FC } from 'react';
import { useTranslation } from 'react-i18next';
// Components
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
// Icons
import { FaUndo, FaRedo } from 'react-icons/fa';
// Hooks
import { IVersionHistory } from './useVersionHistory';

type Props = {
  history: IVersionHistory;
  /** True while the agent is streaming a reply — jumping versions mid-stream
   *  would race the in-flight merge effect in index.tsx, so both buttons are
   *  disabled regardless of canUndo/canRedo until it settles. */
  disabled?: boolean;
};

// Sits in the dialog header, next to "Edit style" / "Save". Buttons disable
// themselves when there's nothing to undo/redo — history.canUndo/canRedo
// already fold in the nested-dialog lock — or while `disabled` (agent
// streaming) is true.
const VersionHistoryControls: FC<Props> = ({ history, disabled = false }) => {
  const { t } = useTranslation();
  const { undo, redo, canUndo, canRedo, position, total, currentLabel } = history;

  if (total === 0) return null;

  return (
    <div className="flex items-center gap-1 pr-2 mr-1 border-r border-white border-opacity-20">
      <Tooltip title={t('dialogs.quizAgent.undoTooltip')}>
        <IconButton onClick={undo} disabled={disabled || !canUndo} size="sm">
          <FaUndo />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('dialogs.quizAgent.versionTooltip', { position, total, label: currentLabel })}>
        <span className="text-xs font-medium text-white text-opacity-70 select-none px-1 tabular-nums">
          {position}/{total}
        </span>
      </Tooltip>
      <Tooltip title={t('dialogs.quizAgent.redoTooltip')}>
        <IconButton onClick={redo} disabled={disabled || !canRedo} size="sm">
          <FaRedo />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default VersionHistoryControls;
